import React, { useContext, useEffect, useMemo, useState } from 'react'
import Stack from '@mui/material/Stack'
import {
  Autocomplete,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  TextField,
  Typography,
  Box,
} from '@mui/material'
import PCOptions from './presentingComplaintOptions'
import { EPCRContext } from '../EPCR'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import NextButton from '../NextButton'
import fastTrackPresentingComplaints from './FastTrackOptions'
import SimpleTextField from '../../../../components/Forms/SimpleTextField'
import { searchComplaints } from './complaintsSearch'
import API from '../../../../services/API'
import { isFullLayoutMode } from '../formTypes'
import {
  OCCURRED_BEFORE_EVENT_OPTIONS,
  hasInjuryComplaint,
  toOccurredAtEvent,
  toOccurredBeforeEvent,
} from './injuryComplaints'

const HistoryModule = () => {
  const { formValues, handleChange } = useContext(EPCRContext)
  const [inputValue, setInputValue] = useState('')
  const [didYouMean, setDidYouMean] = useState(null)
  const [fastTrackOptionsList, setFastTrackOptionsList] = useState(
    fastTrackPresentingComplaints,
  )

  useEffect(() => {
    API.get(
      '/settings?select=patientRecordSettings.fastTrackPresentingComplaints',
    ).then((res) => {
      if (res.status === 200) {
        if (
          res?.data?.patientRecordSettings?.fastTrackPresentingComplaints
            ?.length
        ) {
          setFastTrackOptionsList(
            res.data.patientRecordSettings.fastTrackPresentingComplaints,
          )
        }
      }
    })
  }, [])

  // Flatten grouped options into [{category,label,keywords}]
  const presentingComplaintOptions = useMemo(() => {
    const out = []
    Object.entries(PCOptions).forEach(([category, items]) => {
      items.forEach((x) => {
        // support either strings (old) or objects (new)
        if (typeof x === 'string') {
          out.push({ category, label: x, keywords: [] })
        } else {
          out.push({ category, label: x.label, keywords: x.keywords || [] })
        }
      })
    })
    // stable alphabetical sort within category
    out.sort((a, b) =>
      (a.category + a.label).localeCompare(b.category + b.label),
    )
    return out
  }, [])

  const selected = formValues?.history?.presentingComplaint ?? [] // stored as array of strings (labels)

  const askOccurredBeforeEvent =
    formValues?.incident?.incidentType === 'Event' &&
    formValues?.mode !== 'Welfare' &&
    hasInjuryComplaint(selected)

  const addComplaintLabel = (labelToAdd) => {
    if (!labelToAdd) return
    if (selected.includes(labelToAdd)) return

    handleChange({
      moduleName: 'history',
      field: 'presentingComplaint',
      value: [...selected, labelToAdd],
    })
  }

  return (
    <Stack gap={2}>
      <EPCRCard>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="title2">
              History{formValues.mode === 'Fast Track' && ' & Assessment'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              {isFullLayoutMode(formValues.mode) ? (
                <Autocomplete
                  multiple
                  options={presentingComplaintOptions}
                  groupBy={(o) => o.category}
                  getOptionLabel={(o) => o.label || ''}
                  value={selected}
                  inputValue={inputValue}
                  onInputChange={(e, newValue) => setInputValue(newValue)}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value
                  }
                  // We store values as strings (labels)
                  onChange={(e, value) => {
                    // value is array of (strings OR option objects) depending on interaction
                    const labels = value.map((v) =>
                      typeof v === 'string' ? v : v?.label,
                    )
                    // Remove any accidental empty/undefined
                    const cleaned = labels.filter(Boolean)

                    handleChange({
                      moduleName: 'history',
                      field: 'presentingComplaint',
                      value: cleaned,
                    })
                  }}
                  // Replace MUI filtering with your ranked search
                  filterOptions={(options, state) => {
                    const q = state.inputValue || ''
                    if (!q) {
                      setDidYouMean(null)
                      return options
                    }
                    const { results, didYouMean: dym } = searchComplaints(
                      // IMPORTANT: pass the *grouped object* if your searchComplaints expects it,
                      // but in the version I gave earlier it accepts the grouped object.
                      // Here we’ve flattened already, so we’ll use an adapter:
                      // We'll emulate the expected shape by passing a grouped object is awkward,
                      // so instead we’ll run search against a synthetic grouped object:
                      // (simpler: update searchComplaints to accept the flat list; if you want I’ll do that.)
                      // For now: implement flat search right here:
                      {},
                      q,
                    )

                    // If your searchComplaints version expects a flat array instead,
                    // replace the above with:
                    // const { results, didYouMean: dym } = searchComplaintsFlat(options, q)

                    // Because the above call uses {}, it won't work unless your function supports flat lists.
                    // So instead, do the flat search inline with the original searchComplaints logic:
                    const flat = options
                    const norm = (s) =>
                      (s || '')
                        .toLowerCase()
                        .trim()
                        .replace(/[^a-z0-9\s/]/g, '')
                        .replace(/\s+/g, ' ')

                    const qN = norm(q)
                    const scored = flat
                      .map((item) => {
                        const labelN = norm(item.label)
                        const kws = (item.keywords || []).map(norm)

                        let score = -Infinity
                        if (!qN) score = -Infinity
                        else if (labelN === qN) score = 1000
                        else if (labelN.startsWith(qN)) score = 800
                        else if (labelN.includes(qN)) score = 650
                        else {
                          for (const kw of kws) {
                            if (kw === qN) {
                              score = 600
                              break
                            }
                            if (kw.startsWith(qN)) {
                              score = 520
                              break
                            }
                            if (kw.includes(qN)) {
                              score = 450
                              break
                            }
                          }
                        }

                        return { item, score }
                      })
                      .filter((x) => x.score > -Infinity)
                      .sort((a, b) => b.score - a.score)
                      .slice(0, 50) // cap results

                    // "Did you mean" if no results
                    if (scored.length === 0 && qN.length >= 3) {
                      // cheap suggestion: closest by startsWith/contains first, else null
                      const contains = flat.find((i) =>
                        norm(i.label).includes(qN.slice(0, 3)),
                      )
                      setDidYouMean(contains?.label || null)
                    } else {
                      setDidYouMean(null)
                    }

                    return scored.map((x) => x.item)
                  }}
                  renderTags={(value, getTagProps) => (
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {value.map((tag, index) => {
                        const { key, ...chipProps } = getTagProps({ index })
                        return <Chip key={key} label={tag} {...chipProps} />
                      })}
                    </Stack>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Presenting Complaint"
                      helperText={
                        didYouMean ? (
                          <Box>
                            No results. Did you mean{' '}
                            <Box
                              component="span"
                              sx={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                              onMouseDown={(e) => {
                                // prevent Autocomplete losing focus before click
                                e.preventDefault()
                              }}
                              onClick={() => {
                                addComplaintLabel(didYouMean)
                                setInputValue('')
                                setDidYouMean(null)
                              }}
                            >
                              “{didYouMean}”
                            </Box>
                            ?
                          </Box>
                        ) : null
                      }
                    />
                  )}
                />
              ) : (
                <Autocomplete
                  multiple
                  options={fastTrackOptionsList}
                  value={selected}
                  onChange={(e, value) => {
                    // fastTrack list might be strings; keep strings
                    handleChange({
                      moduleName: 'history',
                      field: 'presentingComplaint',
                      value: value.filter(Boolean),
                    })
                  }}
                  renderTags={(value, getTagProps) => (
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {value.map((tag, index) => {
                        const { key, ...chipProps } = getTagProps({ index })
                        return <Chip key={key} label={tag} {...chipProps} />
                      })}
                    </Stack>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Presenting Complaint" />
                  )}
                />
              )}
            </FormControl>
          </Grid>

          {askOccurredBeforeEvent && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="occurred-before-event-label">
                  Did the injury occur before this event?
                </InputLabel>
                <Select
                  value={toOccurredBeforeEvent(
                    formValues?.incident?.injuryOccurredAtEvent,
                  )}
                  onChange={(e) =>
                    handleChange({
                      moduleName: 'incident',
                      field: 'injuryOccurredAtEvent',
                      value: toOccurredAtEvent(e.target.value),
                    })
                  }
                  label="Did the injury occur before this event?"
                  labelId="occurred-before-event-label"
                >
                  {OCCURRED_BEFORE_EVENT_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Whether the patient arrived with this injury, rather than
                  sustaining it at the event. Counted in the Post Event
                  Analysis.
                </FormHelperText>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              value={formValues?.history?.historyPresentingComplaint ?? ''}
              label="History of Presenting Complaint"
              fullWidth
              onChange={(e) =>
                handleChange({
                  moduleName: 'history',
                  field: 'historyPresentingComplaint',
                  value: e.target.value,
                })
              }
              multiline
              minRows={4}
            />
          </Grid>

          {formValues.mode === 'Fast Track' && (
            <Grid item xs={12}>
              <SimpleTextField
                value={formValues?.assess?.freeText}
                onChange={(e) =>
                  handleChange({
                    moduleName: 'assess',
                    field: 'freeText',
                    value: e.target.value,
                  })
                }
                multiline
                label="Assessment"
              />
            </Grid>
          )}
        </Grid>
      </EPCRCard>

      <NextButton newModule="Assess" />
    </Stack>
  )
}

export default HistoryModule
